import * as React from 'react';
import { TabInfoLinearPages, TabType, TabInfo } from '../../reducers/pages';
import { find, findIndex } from '../../../../../common/src/ts/utils';
import { Dimension } from '../../types/common';
import { IndexedSpreadBlock } from '../../types/epub';
import { thumbnailWidth, thumbnailHeight } from '../../constants';
import ToolIconButton from '../common/ToolIconButton';
import { connect } from 'react-redux';
import { AppStates } from '../../reducers/app';
import { moveToPage } from '../../actions/PageAction';
import { AppAction } from '../../actions/AppAction';
import { Dispatch } from 'redux';
import { Rectangle } from 'ark-common';
import ThumbnailSpread, { EmptySpread } from '../thumbnail/ThumbnailSpread';
import { getBlockIndex } from '../../utils/epub';

const rowStyle: Readonly<React.CSSProperties> = {
  marginTop: 16,
  marginBottom: 16,
  padding: 0,
  display: 'flex',
};

const buttonStyle: Readonly<React.CSSProperties> = {
  position: 'absolute',
  left: '50%',
  marginLeft: -24,
  padding: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
};

export interface PageListOwnProps {
  readonly scale: number;
  readonly cols: number;
  readonly rows: number;
  readonly onClick?: (page: number) => any;
}

interface PageListProps extends PageListOwnProps {
  readonly tabs: ReadonlyArray<TabInfo>;
  readonly pageBlocks: ReadonlyArray<IndexedSpreadBlock>;
  readonly dispatch: Dispatch<AppAction>;
  readonly tabPanelAreaRect: Readonly<Rectangle>;
}

interface PageListState {
  readonly blockIndex: number;
}

class PageList extends React.Component<PageListProps, PageListState> {
  public state = { blockIndex: -1 };

  private getBlockIndex(page: number) {
    const { props: { pageBlocks, rows, cols }, state: { blockIndex } } = this;
    return blockIndex < 0 ? getBlockIndex(pageBlocks, page, rows * cols) : blockIndex;
  }

  private getPageGroup(page: number) {
    const { props: { pageBlocks, rows, cols } } = this;
    const index = this.getBlockIndex(page);
    return pageBlocks.slice(index, index + rows * cols);
  }

  private getCurrentPage() {
    const tab = this.getLinearTab();
    return tab ? tab.currentPage : 0;
  }

  private handlePagesPopupMove(count: number) {
    const { props: { pageBlocks, rows, cols }, state } = this;
    const currentPage = this.getCurrentPage();
    const index = this.getBlockIndex(currentPage);
    const maxIndex = pageBlocks.length - 1 - (pageBlocks.length - 1) % (rows * cols);
    this.setState({
      ...state,
      blockIndex: Math.max(0, Math.min(maxIndex, index + count * rows * cols)),
    });
  }

  private handlePagesPopupNext = () => this.handlePagesPopupMove(1);
  private handlePagesPopupPrev = () => this.handlePagesPopupMove(-1);

  private getLinearTabIndex() {
    return findIndex(this.props.tabs, tab => tab.type === TabType.LinearPages);
  }

  private getLinearTab() {
    type Tab = TabInfoLinearPages | undefined;
    return find(this.props.tabs, tab => tab.type === TabType.LinearPages) as Tab;
  }

  private moveToPage = (page: number) => {
    moveToPage(this.getLinearTabIndex(), page, this.props.dispatch);
  }

  private calcThumbnailSize(): Dimension {
    const { tabPanelAreaRect, scale, rows, cols } = this.props;
    const maxWidth = thumbnailWidth * scale;
    const maxHeight = thumbnailHeight * scale;
    const width = Math.max(0, Math.min(maxWidth, (tabPanelAreaRect.width - 84) / (cols * 2)));
    const height = Math.max(0, Math.min(maxHeight, (tabPanelAreaRect.height - 80) / rows));
    const w1 = width * thumbnailHeight;
    const h1 = height * thumbnailWidth;
    return {
      width: w1 <= h1 ? width : h1 / thumbnailHeight,
      height: w1 >= h1 ? height : w1 / thumbnailWidth,
    };
  }

  private createPages() {
    const { props: { rows, cols } } = this;
    const currentPage = this.getCurrentPage();
    const pageGroup = this.getPageGroup(currentPage);
    const thumbnailSize = this.calcThumbnailSize();

    const thumbnails: React.ReactElement<any>[] = pageGroup.map(
      spread => <ThumbnailSpread
        key={`page-${spread.index}`}
        spread={spread}
        currentPage={currentPage}
        thumbnailSize={thumbnailSize}
        onClick={this.props.onClick ? this.props.onClick : this.moveToPage} />,
    );
    while (thumbnails.length < rows * cols) {
      thumbnails.push(<EmptySpread
        key={`empty-${thumbnails.length}`}
        thumbnailSize={thumbnailSize} />);
    }

    const result: React.ReactElement<any>[] = [];
    for (let index = 0; index < thumbnails.length; index += cols) {
      result.push(<div key={`${thumbnails[index].key}`} style={rowStyle}>
        {thumbnails.slice(index, index + cols)}
      </div>);
    }
    return result;
  }

  public render() {
    // TODO: 上下ボタンは適当なので後でちゃんと作り直す
    return <div style={{ paddingLeft: 8, paddingRight: 8, position: 'relative' }}>
      {this.createPages()}
      <div style={{ ...buttonStyle, top: -24 }}>
        <ToolIconButton text="∧" margin={false} onClick={this.handlePagesPopupPrev} />
      </div>
      <div style={{ ...buttonStyle, bottom: -24 }}>
        <ToolIconButton text="∨" margin={false} onClick={this.handlePagesPopupNext} />
      </div>
    </div>;
  }
}

function mapStateToProps(appStates: AppStates) {
  return {
    tabPanelAreaRect: appStates.view.tabPanelAreaRect,
    tabs: appStates.pages.tabs,
    pageBlocks: appStates.thumbnail.pageBlocks,
  };
}

export default connect(mapStateToProps)(PageList);
